import { Dispatch, SetStateAction, VFC, useContext } from 'react';
import { useHooks } from './useHooks';
import { Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { SiteProcessDatePicker } from '../../input/ProcessDatePicker';
import { TGanttSearchForm } from '../../../../types/process';
import { useStyles } from './useStyles';
import LoadingComponent from '../../../LoadingComponent';
import { DEFAULT_MESSAGE_NO_DATA } from '../../../../const/Const';
import { SiteProcessAddModal } from '../ProcessAddModal';
import { SiteProcessEditModal } from '../ProcessEditModal';
import { SiteProcessCopyModal } from '../ProcessCopyModal';
import { OverallPeriodModal } from '../OverallPeriodModal';
import { AllSiteProcessDeleteModal } from '../AllSiteProcessDeleteModal';
import { TSelectItem } from '../../../../types/common';
import { SiteProcessGanttChart } from '../ProcessGanttChart';
import {
  GANTT_CHART_HTML_ID,
  GANTT_SEARCH_HTML_ID
} from '../../../../const/ProcessConst';
import { SiteTaskListViewModal } from '../../task/TaskListViewModal';
import Loading from '../../../molecules/Loading';
import { AuthInfoContext } from '../../../../AuthContextProvider';
import { ROLE } from '../../../../const/Const';

export type TProps = {
  constructId: number;
  setGanttFromTo: Dispatch<
    SetStateAction<{
      from: string;
      to: string;
    }>
  >;
  isOpenAddSiteProcessModal: boolean;
  setIsOpenAddSiteProcessModal: Dispatch<boolean>;
  parentSiteProcessId?: number;
  setParentSiteProcessId: Dispatch<number>;
  reRenderSiteProcessData: () => void;
  isLoading: boolean;
  setIsLoading: Dispatch<boolean>;
  isOpenCopy: boolean;
  setIsOpenCopy: Dispatch<SetStateAction<boolean>>;
  isOpenEditPeriod: boolean;
  setIsOpenEditPeriod: Dispatch<SetStateAction<boolean>>;
  isOpenAllDelete: boolean;
  setIsOpenAllDelete: Dispatch<SetStateAction<boolean>>;
  constructListVal: TSelectItem;
  setHasSiteProcess: Dispatch<boolean>;
};

export const SiteProcessGanttView: VFC<TProps> = props => {
  // 検索フォーム関連
  const methods: UseFormReturn<TGanttSearchForm> = useForm({
    mode: 'onChange'
  });
  const {
    reset,
    getValues,
    clearErrors,
    formState: { errors }
  } = methods;

  const {
    constructId,
    setGanttFromTo,
    isOpenAddSiteProcessModal,
    isOpenCopy,
    setIsOpenCopy,
    isOpenAllDelete,
    setIsOpenAllDelete,
    isOpenEditPeriod,
    setIsOpenEditPeriod,
    setIsOpenAddSiteProcessModal,
    parentSiteProcessId,
    setParentSiteProcessId,
    reRenderSiteProcessData,
    isLoading,
    setIsLoading,
    constructListVal,
    setHasSiteProcess
  } = props;
  const {
    calendarRef,
    initialHeight,
    initialGanttData,
    searchStartDate,
    searchEndDate,
    handleChangeStartDatePicker,
    handleChangeEndDatePicker,
    handleAfterAddSiteProcess,
    handleAfterClearSiteProcess,
    isOpenEditSiteProcessModal,
    setIsOpenEditSiteProcessModal,
    editSiteProcessId,
    handleAfterEditSiteProcess,
    handleAfterDeleteSiteProcess,
    isOpenSiteTaskListModal,
    setIsOpenSiteTaskListModal,
    siteProcessIdForSiteTask,
    handleAfterChangeSiteTask,
    handleEditSiteProcess,
    handleResourceOrderChange,
    initialVisibleRange,
    isOpenAllSiteProcess,
    handleOpenAllSiteProcess,
    initialResourceAreaColumns,
    isDisplayAll,
    handleDisplayAll,
    initialResourceAreaWidth,
    formValidatePeriod,
    isLoadingForModal,
    includeChildSiteProcess,
    setIncludeChildSiteProcess,
    handleChangeConstructId,
    updateDisplayRange,
    constructList
  } = useHooks({
    constructId,
    setGanttFromTo,
    isOpenAddSiteProcessModal,
    setIsOpenAddSiteProcessModal,
    parentSiteProcessId,
    setParentSiteProcessId,
    reset,
    getValues,
    clearErrors,
    reRenderSiteProcessData,
    setIsLoading
  });

  const classes = useStyles();
  const [authInfo, setAuthInfo] = useContext(AuthInfoContext);

  return (
    <>
      {/* 検索フォーム部分 */}
      <div className={classes.searchContainer} id={GANTT_SEARCH_HTML_ID}>
        <div className={classes.searchPeriodWrapper}>
          <FormProvider {...methods}>
            <SiteProcessDatePicker
              name='start_date'
              label='開始日'
              required={true}
              fullWidth={true}
              toDate={searchEndDate}
              onChange={handleChangeStartDatePicker}
              className={classes.dateInput}
              errorMessage={
                errors?.start_date?.type === 'required'
                  ? errors.start_date.message
                  : errors?.end_date?.type === 'required'
                  ? undefined
                  : errors?.start_date?.message || errors?.end_date?.message
              }
              errorMessageStyle={
                errors?.start_date?.type === 'required'
                  ? undefined
                  : { display: 'none' }
              }
              validateFunc={formValidatePeriod}
            />
            <div className={classes.waveDash}>
              <Typography>～</Typography>
            </div>
            <SiteProcessDatePicker
              name='end_date'
              label='終了日'
              required={true}
              fullWidth={true}
              fromDate={searchStartDate}
              onChange={handleChangeEndDatePicker}
              className={classes.dateInput}
              errorMessage={
                errors?.end_date?.type === 'required'
                  ? errors.end_date.message
                  : errors?.start_date?.type === 'required'
                  ? undefined
                  : errors?.start_date?.message || errors?.end_date?.message
              }
              errorMessageStyle={
                errors?.end_date?.type === 'required'
                  ? undefined
                  : { display: 'none' }
              }
              validateFunc={formValidatePeriod}
            />
            {errors?.start_date?.type !== 'required' &&
              errors?.end_date?.type !== 'required' && (
                <div className={classes.helperText}>
                  {errors?.start_date?.message || errors?.end_date?.message}
                </div>
              )}
          </FormProvider>
        </div>
        <div className={classes.displaySwitchWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isOpenAllSiteProcess}
                onChange={handleOpenAllSiteProcess}
                name='openProcess'
                size='small'
                color='default'
              />
            }
            label={
              <Typography className={classes.checkBoxLabel}>
                全工程を開く
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isDisplayAll}
                onChange={handleDisplayAll}
                name='openAll'
                size='small'
                color='default'
              />
            }
            label={
              <Typography className={classes.checkBoxLabel}>
                詳細を表示する
              </Typography>
            }
          />
        </div>
      </div>

      {/* ガントチャート */}
      <div id={GANTT_CHART_HTML_ID}>
        {isLoading ? (
          <LoadingComponent />
        ) : (
          <>
            {/* モーダルclose時のローディング処理 */}
            <Loading open={isLoadingForModal} />

            <SiteProcessGanttChart
              constructId={constructId}
              calendarRef={calendarRef}
              eventStartEditable={
                authInfo?.role != ROLE['OUTSIDE_PARTNER'] ? true : false
              }
              eventDurationEditable={
                authInfo?.role != ROLE['OUTSIDE_PARTNER'] ? true : false
              }
              displayDailyReport={true}
              initialHeight={initialHeight}
              initialGanttData={initialGanttData}
              initialVisibleRange={initialVisibleRange}
              initialResourceAreaColumns={initialResourceAreaColumns}
              initialResourceAreaWidth={initialResourceAreaWidth}
              handleEditSiteProcess={handleEditSiteProcess}
              handleResourceOrderChange={handleResourceOrderChange}
            />
          </>
        )}
      </div>
      {/* 工程追加モーダル */}
      <SiteProcessAddModal
        isOpen={isOpenAddSiteProcessModal}
        setIsOpen={setIsOpenAddSiteProcessModal}
        afterSubmit={handleAfterAddSiteProcess}
        constructId={constructId}
        parentSiteProcessId={parentSiteProcessId}
      />
      {/* 編集モーダル */}
      <SiteProcessEditModal
        isOpen={isOpenEditSiteProcessModal}
        setIsOpen={setIsOpenEditSiteProcessModal}
        siteProcessId={editSiteProcessId}
        afterUpdate={handleAfterEditSiteProcess}
        afterDelete={handleAfterDeleteSiteProcess}
      />

      <SiteProcessCopyModal
        isOpen={isOpenCopy}
        setIsOpen={setIsOpenCopy}
        constructList={constructList}
        afterSubmit={handleChangeConstructId}
        currentConstructId={constructId}
        setHasSiteProcess={setHasSiteProcess}
      />

      <AllSiteProcessDeleteModal
        isOpen={isOpenAllDelete}
        setIsOpen={setIsOpenAllDelete}
        afterSubmit={handleAfterClearSiteProcess}
        constructId={constructId}
        constructName={
          constructListVal.label_name_only || constructListVal.label
        }
      />
      {/* 工期設定モーダル */}
      <OverallPeriodModal
        isOpen={isOpenEditPeriod}
        setIsOpen={setIsOpenEditPeriod}
        constructId={constructId}
      />
      {/* タスク一覧モーダル */}
      <SiteTaskListViewModal
        isOpen={isOpenSiteTaskListModal}
        setIsOpen={setIsOpenSiteTaskListModal}
        constructId={constructId}
        siteProcessId={siteProcessIdForSiteTask}
        afterUpdate={handleAfterChangeSiteTask}
        includeChildSiteProcess={includeChildSiteProcess}
        setIncludeChildSiteProcess={setIncludeChildSiteProcess}
      />
    </>
  );
};
